/** Post-move assessment core — no React; safe for api/scan.js */

function pctMove(current, prior) {
  if (current == null || prior == null || prior === 0) return null;
  return ((current - prior) / prior) * 100;
}

function safeMax(values, fallback) {
  if (!values?.length) return fallback ?? null;
  let max = values[0];
  for (let i = 1; i < values.length; i += 1) {
    if (values[i] > max) max = values[i];
  }
  return max;
}

function safeMin(values, fallback) {
  if (!values?.length) return fallback ?? null;
  let min = values[0];
  for (let i = 1; i < values.length; i += 1) {
    if (values[i] < min) min = values[i];
  }
  return min;
}

export function computeMoveMetrics(closes, opts = {}) {
  const { highs, lows, price: priceIn } = opts;
  if (!closes?.length) {
    return {
      move1d: null,
      move5d: null,
      move21d: null,
      move63d: null,
      pctFromRecentHigh: null,
      pctFromRecentLow: null,
      recentHigh20: null,
      recentLow20: null,
    };
  }

  const price = priceIn ?? closes[0];
  const close0 = closes[0];
  const close1d = closes.length > 1 ? closes[1] : close0;
  const close5d = closes.length > 4 ? closes[4] : close0;
  const close21d = closes.length > 20 ? closes[20] : close0;
  const close63d = closes.length > 62 ? closes[62] : close0;

  const highSource = highs?.length ? highs : closes;
  const lowSource = lows?.length ? lows : closes;
  const recent20Highs = highSource.slice(0, Math.min(20, highSource.length));
  const recent20Lows = lowSource.slice(0, Math.min(20, lowSource.length));
  const recentHigh20 = safeMax(recent20Highs, price);
  const recentLow20 = safeMin(recent20Lows, price);

  return {
    move1d: pctMove(close0, close1d),
    move5d: pctMove(close0, close5d),
    move21d: pctMove(close0, close21d),
    move63d: pctMove(close0, close63d),
    pctFromRecentHigh: recentHigh20 != null ? pctMove(close0, recentHigh20) : null,
    pctFromRecentLow: recentLow20 != null ? pctMove(close0, recentLow20) : null,
    recentHigh20,
    recentLow20,
  };
}

function resolveVerdictLabel({ verdictLabel, verdict, signal, action, buyScore }) {
  if (verdictLabel) return verdictLabel;
  if (verdict) return verdict;
  const sig = signal;
  if (sig && sig !== "WAIT" && sig !== "NO_SIGNAL") {
    const map = {
      SWING_LONG: "SWING LONG",
      BOTTOM_CONFIRMED: "BOTTOM CONFIRMED",
      BOTTOM_FORMING: "BOTTOM FORMING",
      TOP_CONFIRMED: "TOP CONFIRMED",
      TOP_FORMING: "TOP FORMING",
    };
    return map[sig] || sig;
  }
  if (typeof buyScore === "number") {
    if (buyScore >= 6) return "CONSIDER CALLS";
    if (buyScore >= 4) return "WATCH";
  }
  return action || "WAIT";
}

function hasFlag(flags, name) {
  return flags.includes(name);
}

function fmtPct(n, signed = true) {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toFixed(1);
  if (!signed) return `${abs}%`;
  return `${n >= 0 ? "+" : "-"}${abs}%`;
}

export function generatePostMoveAssessment(metrics, opts = {}) {
  const {
    ema20Val,
    ema8Val,
    t1,
    verdictLabel,
    verdict,
    signal,
    action,
    buyScore,
    rvolRatio,
    price,
    inPosition = false,
  } = opts;

  const {
    move1d, move5d, move21d, move63d, pctFromRecentHigh,
  } = metrics || {};

  const flags = [];
  const context = [];
  let riskLevel = "NORMAL";
  const bump = (level) => {
    if (level === "HIGH") riskLevel = "HIGH";
    else if (level === "ELEVATED" && riskLevel !== "HIGH") riskLevel = "ELEVATED";
  };

  const vLabel = resolveVerdictLabel({ verdictLabel, verdict, signal, action, buyScore });

  if (move1d != null) {
    if (move1d > 5) {
      flags.push("EXCEPTIONAL_UP_DAY");
      context.push(`Exceptional ${fmtPct(move1d)} move today — unusual single-day move`);
      bump("ELEVATED");
    } else if (move1d > 3) {
      flags.push("STRONG_UP_DAY");
      context.push(`Price surged ${fmtPct(move1d)} today`);
    }
    if (move1d < -5) {
      flags.push("EXCEPTIONAL_DOWN_DAY");
      context.push(`Exceptional ${fmtPct(move1d)} drop today`);
      bump("ELEVATED");
    } else if (move1d < -3) {
      flags.push("STRONG_DOWN_DAY");
      context.push(`Price dropped ${fmtPct(move1d)} today`);
    }
  }

  if (move5d != null) {
    if (move5d > 15) {
      flags.push("PARABOLIC_WEEKLY");
      context.push(`Parabolic ${fmtPct(move5d)} weekly move — high exhaustion risk`);
      bump("HIGH");
    } else if (move5d > 8) {
      flags.push("EXTENDED_WEEKLY");
      context.push(`Up ${fmtPct(move5d)} this week — extended short term`);
      bump("ELEVATED");
    }
    if (move5d < -8) {
      flags.push("OVERSOLD_WEEKLY");
      context.push(`Down ${fmtPct(move5d)} this week — potential bounce territory`);
    }
  }

  if (move21d != null) {
    if (move21d > 75) {
      flags.push("EXTREME_EXTENSION");
      context.push(`Up ${fmtPct(move21d)} in 30 days — extreme extension. Signals may still be bullish but risk/reward is significantly reduced.`);
      bump("HIGH");
    } else if (move21d > 40) {
      flags.push("PARABOLIC_MONTHLY");
      context.push(`Up ${fmtPct(move21d)} in 30 days — parabolic extension, mean reversion risk elevated`);
      bump("HIGH");
    } else if (move21d > 20) {
      flags.push("EXTENDED_MONTHLY");
      context.push(`Up ${fmtPct(move21d)} in 30 days — significant run from base`);
      bump("ELEVATED");
    }
    if (move21d < -20) {
      flags.push("DEEPLY_OVERSOLD_MONTHLY");
      context.push(`Down ${fmtPct(move21d)} in 30 days — deep oversold territory`);
    }
  }

  if (move63d != null) {
    if (move63d > 100) {
      flags.push("DOUBLED");
      context.push(`Stock has doubled (${fmtPct(move63d)}) in 3 months — late stage extension likely`);
      bump("HIGH");
    } else if (move63d > 50) {
      flags.push("MAJOR_RUN");
      context.push(`Up ${fmtPct(move63d)} over 3 months — substantial advance from base`);
    }
  }

  if (pctFromRecentHigh != null) {
    if (pctFromRecentHigh >= -0.2) {
      flags.push("AT_HIGHS");
      context.push("Price at or very near recent highs");
    } else if (pctFromRecentHigh >= -1) {
      flags.push("AT_20D_HIGH");
      context.push("Price at 20-day high — at resistance zone");
    }
  }

  let entryGuidance = "NORMAL_ENTRY";
  let guidanceText = null;

  if (hasFlag(flags, "PARABOLIC_MONTHLY") || hasFlag(flags, "EXTREME_EXTENSION") || hasFlag(flags, "DOUBLED")) {
    entryGuidance = "DO_NOT_CHASE";
    const period = hasFlag(flags, "DOUBLED") ? "3 months" : "30 days";
    const moveRef = hasFlag(flags, "DOUBLED") ? move63d : move21d;
    guidanceText = `Signal remains ${vLabel} but entry here carries elevated risk. Stock has moved ${fmtPct(moveRef)} in ${period}. Consider waiting for pullback to EMA20${ema20Val != null ? ` ($${Number(ema20Val).toFixed(2)})` : ""} before entering. If already in position: consider taking partial profits.`;
  } else if (hasFlag(flags, "EXTENDED_MONTHLY") || hasFlag(flags, "PARABOLIC_WEEKLY")) {
    entryGuidance = "WAIT_FOR_PULLBACK";
    guidanceText = `Signal is ${vLabel} but short-term extension detected. Ideal entry: pullback to EMA20${ema20Val != null ? ` ($${Number(ema20Val).toFixed(2)})` : ""}${ema8Val != null ? ` or EMA8 ($${Number(ema8Val).toFixed(2)})` : ""}. Current entry has reduced R:R vs entry at support.`;
  } else if (hasFlag(flags, "STRONG_UP_DAY") && !hasFlag(flags, "EXTENDED_MONTHLY")) {
    entryGuidance = "MOMENTUM_ENTRY_OK";
    const volTxt = rvolRatio != null ? `${rvolRatio.toFixed(1)}x` : "—";
    const highTxt = metrics?.recentHigh20 != null ? `$${metrics.recentHigh20.toFixed(2)}` : (price != null ? `$${price.toFixed(2)}` : "—");
    guidanceText = `Strong up day today (${fmtPct(move1d)}). Signal remains ${vLabel}. Momentum entries acceptable if volume confirms (${volTxt} average today). Watch for continuation above ${highTxt}.`;
  } else {
    entryGuidance = "NORMAL_ENTRY";
    guidanceText = null;
  }

  let positionGuidance = null;
  let positionText = null;

  if (inPosition) {
    if (hasFlag(flags, "AT_HIGHS") && t1 != null && price != null && price >= t1 * 0.98) {
      positionGuidance = "NEAR_T1";
      positionText = `Approaching T1 target ($${Number(t1).toFixed(2)}). Consider taking 50% profit here. Move stop to breakeven on remainder.`;
    }
    if (hasFlag(flags, "PARABOLIC_MONTHLY") && move21d != null) {
      positionGuidance = positionGuidance || "CONSIDER_TRIM";
      positionText = positionText || `Stock up ${fmtPct(move21d)} in 30 days. Signal still ${vLabel} but consider trimming to protect gains. Hold remaining position with tighter stop.`;
    }
  }

  if (!flags.length && entryGuidance === "NORMAL_ENTRY" && !positionGuidance) {
    return {
      flags,
      context,
      entryGuidance,
      guidanceText: null,
      positionGuidance: null,
      positionText: null,
      riskLevel: "NORMAL",
      verdictLabel: vLabel,
    };
  }

  return {
    flags,
    context,
    entryGuidance,
    guidanceText,
    positionGuidance,
    positionText,
    riskLevel,
    verdictLabel: vLabel,
  };
}

export function attachPostMoveAssessment(payload) {
  if (!payload?.closes?.length) return payload;
  try {
    const price = payload.closes[0];
    const metrics = computeMoveMetrics(payload.closes, {
      highs: payload.highs,
      lows: payload.lows,
      price,
    });
    payload.move1d = metrics.move1d;
    payload.move5d = metrics.move5d;
    payload.move21d = metrics.move21d;
    payload.move63d = metrics.move63d;
    payload.pctFromRecentHigh = metrics.pctFromRecentHigh;
    payload.pctFromRecentLow = metrics.pctFromRecentLow;
    payload.recentHigh20 = metrics.recentHigh20;
    payload.recentLow20 = metrics.recentLow20;
    const ema20Val = payload.ema20?.value ?? payload.ema20 ?? null;
    const ema8Val = payload.ema8?.value ?? payload.ema8 ?? null;
    payload.postMoveAssessment = generatePostMoveAssessment(metrics, {
      ema20Val,
      ema8Val,
      t1: payload.t1 ?? null,
      verdict: payload.verdict,
      signal: payload.signal,
      action: payload.action,
      buyScore: payload.buyScore ?? payload.score,
      rvolRatio: payload.rvol?.ratio,
      price,
      inPosition: payload.inPosition,
    });
  } catch (e) {
    console.error("[postMove error]", e);
    payload.postMoveAssessment = null;
    payload.move1d = null;
    payload.move5d = null;
    payload.move21d = null;
    payload.move63d = null;
    payload.pctFromRecentHigh = null;
    payload.pctFromRecentLow = null;
    payload.recentHigh20 = null;
    payload.recentLow20 = null;
  }
  return payload;
}

export function refreshPostMoveAssessment(result, opts = {}) {
  if (!result?.closes?.length) return result;
  try {
    const inPosition = opts.inPosition ?? result.inPosition ?? false;
    const price = opts.price ?? result.price ?? result.closes[0];
    const metrics = computeMoveMetrics(result.closes, {
      highs: result.highs,
      lows: result.lows,
      price,
    });
    const postMoveAssessment = generatePostMoveAssessment(metrics, {
      ema20Val: result.ema20?.value ?? result.ema20 ?? null,
      ema8Val: result.ema8?.value ?? result.ema8 ?? null,
      t1: opts.t1 ?? result.t1 ?? null,
      verdict: result.verdict,
      signal: result.signal,
      action: result.action,
      buyScore: result.buyScore,
      rvolRatio: result.rvol?.ratio,
      price,
      inPosition,
    });
    return {
      ...result,
      ...metrics,
      inPosition,
      postMoveAssessment,
    };
  } catch (e) {
    console.error("[postMove error]", e);
    return {
      ...result,
      postMoveAssessment: null,
      move1d: null,
      move5d: null,
      move21d: null,
      move63d: null,
    };
  }
}

export { fmtPct };
